import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";

function PaymentTab({ facilityId }) {
  const [paymentShortCode, setPaymentShortCode] = useState("");
  const [paymentPasskey, setPaymentPasskey] = useState("");
  const [paymentAuthKey, setPaymentAuthKey] = useState("");
  const [paymentModule, setPaymentModule] = useState("");
  const [hasPaymentDetails, setHasPaymentDetails] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [existingPaymentDetails, setExistingPaymentDetails] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [selectedPaymentDetailId, setSelectedPaymentDetailId] = useState(null);
  const [isLoadingPaymentDetails, setIsLoadingPaymentDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false);

  const getPaymentDetails = async () => {
    setIsLoadingPaymentDetails(true);
    setApiError(null);
    try {
      // Using the correct endpoint as per your routes
      const response = await makeRequest2(
        `/api/core/facility_payment_details/get_facility_payment_details/${facilityId}`,
        "GET",
      );

      if (response.success) {
        // Assuming the API returns an array of payment details
        const details = Array.isArray(response.data)
          ? response.data
          : [response.data];

        setPaymentDetails(details);
        setHasPaymentDetails(details.length > 0);
      } else {
        setPaymentDetails([]);
        setHasPaymentDetails(false);
        clearForm();
      }
    } catch (err) {
      setPaymentDetails([]);
      setHasPaymentDetails(false);
      setApiError("Failed to load payment details. Please try again.");
      clearForm();
      if (err.message !== "Facility payment details not found") {
        toastify(err.message, "error");
      }
    } finally {
      setIsLoadingPaymentDetails(false);
    }
  };

  const fetchPaymentDetailById = async (paymentDetailId) => {
    setIsLoadingEditData(true);
    try {
      // First, attempt to use the specific endpoint
      try {
        const response = await makeRequest2(
          `/api/core/facility_payment_details/get_payment_detail_by_id/${facilityId}/${paymentDetailId}`,
          "GET",
        );

        if (response.success && response.data && response.data.success) {
          // The backend returns { success: true, data: {...} }
          // and makeRequest2 wraps it as { success: true, data: backendResponse }
          // So the actual payment data is at response.data.data
          let paymentData = response.data.data;

          if (!paymentData) {
            paymentData = response.data;
          }

          if (!paymentData || !paymentData.shortCode) {
            throw new Error("Unable to extract payment data from API response");
          }

          setPaymentShortCode(paymentData.shortCode || "");
          setPaymentPasskey(paymentData.passkey || "");
          setPaymentAuthKey(paymentData.authorizationKey || "");
          setPaymentModule(paymentData.module || "");
          setExistingPaymentDetails(paymentData);
          setSelectedPaymentDetailId(paymentDetailId);

          toastify("Payment details loaded for editing", "success");
          return true;
        }
      } catch (specificErr) {
        // If specific endpoint fails, we'll try the fallback below
      }

      // Fallback: Use the payment detail we already have from the list
      // This is a workaround for the 404 issue
      const existingDetail = paymentDetails.find(
        (detail) => detail._id === paymentDetailId,
      );
      if (existingDetail) {
        setPaymentShortCode(existingDetail.shortCode || "");
        setPaymentPasskey(existingDetail.passkey || "");
        setPaymentAuthKey(existingDetail.authorizationKey || "");
        setPaymentModule(existingDetail.module || "");
        setExistingPaymentDetails(existingDetail);
        setSelectedPaymentDetailId(paymentDetailId);

        toastify("Payment details loaded for editing (cached)", "info");
        return true;
      } else {
        toastify("Failed to fetch payment detail", "error");
        return false;
      }
    } catch (err) {
      toastify(err.message, "error");
      return false;
    } finally {
      setIsLoadingEditData(false);
    }
  };

  const fetchDefaultPaymentDetails = async () => {
    setIsLoadingDefaults(true);
    try {
      const response = await makeRequest2(
        "/api/core/payment_details/get_default_payment_details",
        "GET",
      );

      if (response.success && response.data.length > 0) {
        const defaultPayment = response.data[0];

        // Update the form state with default values
        setPaymentShortCode(defaultPayment.shortCode);
        setPaymentPasskey(defaultPayment.passkey);
        setPaymentAuthKey(defaultPayment.authorizationKey);
        setPaymentModule(defaultPayment.module);

        toastify("Default payment details loaded successfully", "success");
      } else {
        toastify("No default payment details found", "warning");
      }
    } catch (err) {
      toastify(err.message, "error");
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  const handlePaymentDetailsSubmit = async () => {
    setIsSubmitting(true);
    setApiError(null);
    try {
      if (!paymentShortCode || !paymentModule) {
        throw new Error("Short code and module are required fields");
      }

      // Check if we're in editing mode
      const isEditing =
        showEditForm && existingPaymentDetails && selectedPaymentDetailId;

      // For new records, all fields are required
      if (!isEditing && (!paymentPasskey || !paymentAuthKey)) {
        throw new Error(
          "All fields are required when creating new payment details",
        );
      }

      const data = {
        facility: facilityId,
        shortCode: paymentShortCode,
        module: paymentModule,
      };

      // Include sensitive data based on mode
      if (isEditing) {
        // In edit mode, always include sensitive fields (they're prefilled with current values)
        // If user cleared them, we still send them (backend will handle appropriately)
        data.passkey = paymentPasskey;
        data.authorizationKey = paymentAuthKey;
      } else {
        // In add mode, only include if provided
        if (paymentPasskey) data.passkey = paymentPasskey;
        if (paymentAuthKey) data.authorizationKey = paymentAuthKey;
      }

      let url, method;

      // If we're editing an existing payment detail
      if (isEditing) {
        // Add the _id to the payload for update
        data._id = selectedPaymentDetailId;
        url = `/api/core/facility_payment_details/update_payment_details`;
        method = "PUT";
      } else {
        // We're adding a new payment detail
        url = `/api/core/facility_payment_details/add_payment_details`;
        method = "POST";
      }

      // First request - to normal server
      const response = await makeRequest2(url, method, data);

      if (response.success) {
        const actionType = isEditing ? "updated" : "added";
        toastify(
          `Payment details ${actionType} successfully to main server`,
          "success",
        );

        // Second request - to MPesa service (only for new payment details)
        if (!isEditing) {
          try {
            // Prepare data for MPesa addPaymentDetails endpoint
            const mpesaData = {
              shortCode: paymentShortCode,
              passkey: paymentPasskey,
              authorizationKey: paymentAuthKey,
              module: paymentModule,
              facilityId: facilityId,
            };

            const mpesaResponse = await axios.post(
              process.env.REACT_APP_MPESA_PAYMENT_DETAILS_URL,
              mpesaData,
              {
                headers: {
                  "Content-Type": "application/json",
                },
                timeout: 10000, // 10 second timeout
              },
            );

            if (mpesaResponse.status === 200 || mpesaResponse.status === 201) {
              toastify("Payment details have been added to MPesa", "success");
            } else {
              throw new Error(
                `MPesa service returned status: ${mpesaResponse.status}`,
              );
            }
          } catch (mpesaErr) {
            // Still continue even if MPesa sync fails, but notify user
            const errorMessage =
              mpesaErr.response?.data?.message ||
              mpesaErr.response?.data?.error ||
              mpesaErr.message ||
              "Unknown error occurred";

            toastify(
              `Warning: Failed to add payment details to MPesa - ${errorMessage}. Main server update was successful.`,
              "warning",
            );
          }
        }

        setShowPaymentForm(false);
        setShowEditForm(false);
        clearForm();
        getPaymentDetails();
      } else {
        throw new Error(
          response.error ||
            `Failed to ${isEditing ? "update" : "save"} payment details`,
        );
      }
    } catch (err) {
      setApiError(err.message);
      toastify(err.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearForm = () => {
    setPaymentShortCode("");
    setPaymentPasskey("");
    setPaymentAuthKey("");
    setPaymentModule("");
    setSelectedPaymentDetailId(null);
    setExistingPaymentDetails(null);
  };

  const handleEdit = async (paymentDetail) => {
    if (paymentDetail && paymentDetail._id) {
      // Clear form first
      clearForm();

      // Save a local copy of the current payment detail in case the API call fails
      setExistingPaymentDetails(paymentDetail);
      setSelectedPaymentDetailId(paymentDetail._id);

      // Show the form immediately
      setShowEditForm(true);

      // Fetch the latest details from the server to ensure we have current data
      const success = await fetchPaymentDetailById(paymentDetail._id);

      if (!success) {
        // If API fails, fallback to using the cached data we have
        setPaymentShortCode(paymentDetail.shortCode || "");
        setPaymentModule(paymentDetail.module || "");
        setPaymentPasskey(paymentDetail.passkey || "");
        setPaymentAuthKey(paymentDetail.authorizationKey || "");

        toastify("Using cached payment data for editing", "warning");
      }
    } else {
      toastify("Invalid payment detail selected", "error");
    }
  };

  const handleCancel = () => {
    setShowPaymentForm(false);
    setShowEditForm(false);
    clearForm();
  };

  useEffect(() => {
    getPaymentDetails();
  }, [facilityId]);

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5>Payment Settings</h5>
            <button
              className="btn btn-primary"
              onClick={() => {
                clearForm();
                setShowPaymentForm(true);
              }}
            >
              <i className="ti ti-plus me-2"></i>Add Payment Details
            </button>
          </div>
          <div className="card-body">
            {/* Show API Error */}
            {apiError && (
              <div className="alert alert-danger" role="alert">
                {apiError}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-2"
                  onClick={() => {
                    setApiError(null);
                    getPaymentDetails();
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Show loading state */}
            {isLoadingPaymentDetails && (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading payment details...</p>
              </div>
            )}

            {/* Show message when no data and not loading */}
            {!isLoadingPaymentDetails &&
              !paymentDetails.length &&
              !apiError && (
                <div className="text-center p-4">
                  <p>No payment details have been added yet.</p>
                  <p>
                    Click "Add Payment Details" to set up payment configuration.
                  </p>
                </div>
              )}

            {/* Display existing payment details in card format */}
            {!isLoadingPaymentDetails && paymentDetails.length > 0 && (
              <div className="row">
                {paymentDetails.map((paymentDetail) => (
                  <div className="col-lg-4 col-xxl-3" key={paymentDetail._id}>
                    <div className="card">
                      <div className="card-body position-relative">
                        <div className="text-center mt-3">
                          <div className="chat-avtar d-inline-flex mx-auto">
                            <i
                              className="ti ti-credit-card me-2"
                              style={{ fontSize: 100 }}
                            ></i>
                          </div>
                          <hr className="my-3 border border-secondary-subtle" />

                          <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                            <p className="mb-0">
                              <strong>Short Code: </strong>
                              {paymentDetail.shortCode}
                            </p>
                          </div>
                          <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                            <p className="mb-0">
                              <strong>Pass Key: </strong>
                              {"********"}
                            </p>
                          </div>
                          <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                            <p className="mb-0">
                              <strong>Authorization Key: </strong>
                              {"********"}
                            </p>
                          </div>
                          <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3">
                            <p className="mb-0">
                              <strong>Module: </strong>
                              {paymentDetail.module}
                            </p>
                          </div>
                          <div className="d-flex align-items-center justify-content-center w-100 mt-3">
                            <button
                              className="btn btn-primary"
                              onClick={() => handleEdit(paymentDetail)}
                            >
                              <i className="ti ti-edit me-2"></i>
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Payment Dialog */}
            <Dialog
              header="Add Payment Details"
              visible={showPaymentForm}
              style={{ width: "40vw" }}
              onHide={() => setShowPaymentForm(false)}
            >
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5>Add Payment Details</h5>
                  <button
                    className="btn btn-secondary"
                    onClick={fetchDefaultPaymentDetails}
                    disabled={isLoadingDefaults}
                  >
                    {isLoadingDefaults ? (
                      <span>
                        <i className="ti ti-loader animate-spin me-2"></i>
                        Loading...
                      </span>
                    ) : (
                      <span>
                        <i className="ti ti-download me-2"></i>
                        Use Default Settings
                      </span>
                    )}
                  </button>
                </div>
                <div className="card-body">
                  <div className="form-group mb-3">
                    <label>
                      Short Code
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Enter Short Code"
                      value={paymentShortCode}
                      onChange={(e) => setPaymentShortCode(e.target.value)}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label>
                      Passkey
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      placeholder="Enter Passkey"
                      value={paymentPasskey}
                      onChange={(e) => setPaymentPasskey(e.target.value)}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label>
                      Authorization Key
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      placeholder="Enter Authorization Key"
                      value={paymentAuthKey}
                      onChange={(e) => setPaymentAuthKey(e.target.value)}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label>
                      Module
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={paymentModule}
                      onChange={(e) => setPaymentModule(e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      <option value="All">All</option>
                      <option value="Water">Water</option>
                      <option value="Electricity">Electricity</option>
                      <option value="Levy">Levy</option>
                      <option value="Lease">Lease</option>
                    </select>
                  </div>

                  <div className="text-end">
                    <button
                      className="btn btn-light me-2"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handlePaymentDetailsSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ti ti-loader animate-spin me-2"></i>
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </Dialog>

            {/* Edit Payment Dialog */}
            <Dialog
              header="Edit Payment Details"
              visible={showEditForm}
              style={{ width: "40vw" }}
              onHide={() => setShowEditForm(false)}
            >
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5>Edit Payment Details</h5>
                  {isLoadingEditData ? (
                    <span>
                      <i className="ti ti-loader animate-spin me-2"></i>
                      Loading payment data...
                    </span>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      onClick={fetchDefaultPaymentDetails}
                      disabled={isLoadingDefaults || isLoadingEditData}
                    >
                      {isLoadingDefaults ? (
                        <span>
                          <i className="ti ti-loader animate-spin me-2"></i>
                          Loading...
                        </span>
                      ) : (
                        <span>
                          <i className="ti ti-download me-2"></i>
                          Use Default Settings
                        </span>
                      )}
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {isLoadingEditData && (
                    <div className="text-center mb-3">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <small className="text-muted ms-2">
                        Fetching current payment details...
                      </small>
                    </div>
                  )}

                  <div className="form-group mb-3">
                    <label>
                      Short Code
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Enter Short Code"
                      value={paymentShortCode}
                      onChange={(e) => setPaymentShortCode(e.target.value)}
                      disabled={isLoadingEditData}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label>
                      Passkey
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      placeholder="Enter New Passkey"
                      value={paymentPasskey}
                      onChange={(e) => setPaymentPasskey(e.target.value)}
                      disabled={isLoadingEditData}
                    />
                    <small className="text-muted">
                      Current value is displayed. You can modify it if needed.
                    </small>
                  </div>

                  <div className="form-group mb-3">
                    <label>
                      Authorization Key
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      placeholder="Enter New Authorization Key"
                      value={paymentAuthKey}
                      onChange={(e) => setPaymentAuthKey(e.target.value)}
                      disabled={isLoadingEditData}
                    />
                    <small className="text-muted">
                      Current value is displayed. You can modify it if needed.
                    </small>
                  </div>

                  <div className="form-group mb-3">
                    <label>
                      Module
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={paymentModule}
                      onChange={(e) => setPaymentModule(e.target.value)}
                      disabled={isLoadingEditData}
                    >
                      <option value="">-- Select --</option>
                      <option value="All">All</option>
                      <option value="Water">Water</option>
                      <option value="Electricity">Electricity</option>
                      <option value="Levy">Levy</option>
                      <option value="Lease">Lease</option>
                    </select>
                  </div>

                  <div className="text-end">
                    <button
                      className="btn btn-light me-2"
                      onClick={handleCancel}
                      disabled={isSubmitting || isLoadingEditData}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handlePaymentDetailsSubmit}
                      disabled={isSubmitting || isLoadingEditData}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ti ti-loader animate-spin me-2"></i>
                          Updating...
                        </>
                      ) : (
                        "Update"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentTab;
